import React from 'react';
import PropTypes from 'prop-types';
import { Form, Icon, Label, Popup, TextArea } from 'semantic-ui-react';
import DeleteModal from './DeleteModal';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Dropdown, FrameCard, Tooltip, ToastContext } from '@weezevent/nacre';
import { LanguageSelector } from '../../nacre/components/LanguageSelector';
import { Link } from 'react-router-dom';
import WidgetEvent from '../../models/widgetEvent';
import { aliases } from '../../i18n/i18n';

const mappingWordingGenerateCode = {
    fr: {
        title: 'Logiciel billetterie en ligne',
        label: 'Billetterie Weezevent'
    },
    en: {
        title: 'Online ticketing software',
        label: 'Weezevent Ticketing'
    },
    es: {
        title: 'Venta de entradas en línea',
        label: 'Venta de entradas Weezevent'
    },
    it: {
        title: 'Biglietteria online',
        label: 'Biglietteria Weezevent'
    },
    de: {
        title: 'Online-Ticketverkauf',
        label: 'Weezevent Ticketverkauf'
    }
};

function getWordingGenerateCode(locale) {
    let wording = mappingWordingGenerateCode['en'];

    if (!locale) {
        return wording;
    }

    let _lang = locale
        .replace('_', '-')
        .split('-')[0]
        .toLowerCase();

    return mappingWordingGenerateCode[_lang] || wording;
}

export function generateCode(widget, lang, color = null) {
    let wording = getWordingGenerateCode(lang);

    return `<a title="${wording.title}" \
href="${widget.code_url(lang, color)}"  \
class="weezevent-widget-integration" target="_blank" \
data-src="${widget.code_url(lang, color)}" \
data-width="${widget.code_width()}" \
data-height="${widget.code_height()}" \
data-resize="${widget.code_resize()}" \
data-nopb="${widget.hide_power_by_weez()}" \
data-type="neo" data-width_auto="${widget.code_resize()}" \
${widget instanceof WidgetEvent ? ` data-noscroll="0" data-id="${widget.event.id}"` : ''}\
>${wording.label}</a>\
\n<script type="text/javascript" src="${window.weezConfig.api.widgetUrl}/weez.js"  async defer></script>`;
}

function MultiWidgetIntegration({ widget }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    let lang = aliases[widget.default_language] || widget.default_language;
    const [widgetLang, setWidgetLang] = React.useState(lang);

    return (
        <div>
            {t('ticket.widget.list.moduleIntegrationBody')}
            <div style={{ margin: '10px 0' }}>
                <LanguageSelector
                    lang={widgetLang}
                    onChange={(event, { value }) => {
                        setWidgetLang(value);
                    }}
                    label={t('ticket.widget.list.languageIntegration')}
                    arrayLangDefault={widget.languages}
                />
            </div>
            <Form>
                <TextArea value={generateCode(widget, widgetLang)} style={{ resize: 'none', fontSize: '10px' }} />
            </Form>
            <Button
                primary
                style={{ margin: '10px 0' }}
                size="mini"
                onClick={() => {
                    navigator.clipboard.writeText(generateCode(widget, widgetLang));
                    toast.success(t('ticket.common.cta.copied_clipboard'));
                }}
                label={t('ticket.common.cta.copy')}
            />
        </div>
    );
}
MultiWidgetIntegration.propTypes = {
    widget: PropTypes.object.isRequired
};

export default function WidgetCard({ organizationId, widget }) {
    const { t } = useTranslation();
    let eventRender = [];
    const [shown, display] = React.useState(false);
    widget.widget_events.forEach(we => {
        eventRender.push(
            <Label key={we.id} className="margin5">
                {we.name}
            </Label>
        );
    });
    widget.events.forEach(e => {
        eventRender.push(
            <Label color="blue" key={e.id}>
                {e.name}
            </Label>
        );
    });
    return (
        <FrameCard
            key={widget.widget_key}
            title={widget.name}
            items={[
                <Dropdown.Item
                    key={0}
                    as="a"
                    item={<Trans i18nKey="ticket.common.action.open">Open</Trans>}
                    href={`${window.weezConfig.api.widgetUrl}/ticket/${widget.widget_key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                />,
                <Dropdown.Item key={1} as={Link} to={`/ticket/O${organizationId}/widgets/${widget.id}`}>
                    <Trans i18nKey="ticket.common.action.edit">Edit</Trans>
                </Dropdown.Item>,
                <Dropdown.Line key={2} />,
                <Dropdown.Item key={3} onClick={() => display(true)} deleteItem>
                    <Trans i18nKey="ticket.common.action.delete">Delete</Trans>
                </Dropdown.Item>
            ]}
        >
            <a href={`${window.weezConfig.api.widgetUrl}/ticket/${widget.widget_key}`} target="_blank" rel="noopener noreferrer">
                <Tooltip triggerOnHover title={t('ticket.common.action.open')}>
                    <Icon color="grey" size="small" name="eye" />
                </Tooltip>
            </a>
            <Popup
                wide={'very'}
                on="click"
                header={t('ticket.widget.list.moduleIntegrationTitle')}
                content={<MultiWidgetIntegration widget={widget} />}
                trigger={
                    <span>
                        <Tooltip triggerOnHover title={t('ticket.widget.integration')}>
                            <Icon color="grey" size="small" name="code" style={{ cursor: 'pointer' }} />
                        </Tooltip>
                    </span>
                }
            />
            {shown && <DeleteModal item={widget} display={display} organizationId={organizationId} />}
            <div>{eventRender}</div>
        </FrameCard>
    );
}

WidgetCard.propTypes = {
    widget: PropTypes.object.isRequired,
    organizationId: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired
};
